import "mapbox-gl/dist/mapbox-gl.css";

import { type CSSProperties, type MouseEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { LngLatBoundsLike, MapRef, ViewState, ViewStateChangeEvent } from "react-map-gl/mapbox";
import { Map as MapGL } from "react-map-gl/mapbox";
import { applyCooperativeGestures } from "./cooperative-gestures";
import { MapControls } from "./MapControls";

const FIT_BOUNDS_DURATION = 500;
const DEFAULT_FIT_BOUNDS_PADDING = 40;
const DEFAULT_MAP_STYLE = "mapbox://styles/nickpellant-travily/cmqsc05y0001i01sehy72gx5p";

/** Symmetric pixel padding, or per-edge padding (all edges required — mapbox `PaddingOptions`). */
export interface MapPaddingObject {
  top: number;
  right: number;
  bottom: number;
  left: number;
}
export type MapPadding = number | MapPaddingObject;

export interface MapProps {
  /** Mapbox public access token. Read this from `import.meta.env.VITE_MAPBOX_ACCESS_TOKEN`. */
  mapboxAccessToken: string | undefined;
  /** Controlled view state; pair with `onMove` to drive panning/zooming. */
  viewState?: Partial<ViewState>;
  /** Initial view state; ignored when `viewState` or `initialBounds` is provided. */
  initialViewState?: Partial<ViewState>;
  /** Initial bounds to fit on first render (e.g. result envelope). */
  initialBounds?: LngLatBoundsLike;
  /**
   * Bounds to imperatively fit when the array reference changes. Pass a stable
   * tuple so the effect doesn't fire on every render.
   */
  fitBounds?: LngLatBoundsLike;
  /** Padding applied to `fitBounds`/`initialBounds`. A number pads all edges equally; pass an
   *  object to reserve space (e.g. a bottom sheet occluding the lower map). */
  fitBoundsPadding?: MapPadding;
  /** Imperatively pan (zoom-preserving `easeTo`) to center a point when this reference changes,
   *  offset by optional per-edge padding so the point clears occluding UI. */
  recenterTo?: { longitude: number; latitude: number; padding?: MapPaddingObject };
  onMove?: (event: ViewStateChangeEvent) => void;
  /** Fires only for user-initiated pan/zoom (mouse, touch, keyboard). Programmatic
   *  moves like `fitBounds`/`flyTo` are filtered out automatically. Use this to drive
   *  search refinement on map navigation. */
  onUserMoveEnd?: (event: ViewStateChangeEvent) => void;
  /** Show the custom map controls (zoom pill, plus the expand toggle when `showFullscreen`). Defaults to true. */
  showControls?: boolean;
  /** Offer the expand-to-fullscreen toggle. Only enable where the map sits below the
   *  main-app top bar (it offsets by `--frame-topbar-h`); defaults to false. */
  showFullscreen?: boolean;
  /** Corner the custom controls anchor to. Use `bottom-right` on maps with content
   *  overlaying the top edge; defaults to `top-right`. */
  controlsPosition?: "top-right" | "bottom-right";
  /** Require ctrl/cmd + scroll to zoom and two fingers to pan, so an inline map inside
   *  a scrolling page doesn't hijack the scroll gesture. Defaults to false. */
  cooperativeGestures?: boolean;
  mapStyle?: string;
  /** Whether the map responds to user input. */
  interactive?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Markers, popups, and overlays composed inside the map. */
  children?: ReactNode;
}

// biome-ignore lint/suspicious/noShadowRestrictedNames: intentional system primitive name aligning with react-map-gl
export function Map({
  mapboxAccessToken,
  viewState,
  initialViewState,
  initialBounds,
  fitBounds,
  fitBoundsPadding = DEFAULT_FIT_BOUNDS_PADDING,
  recenterTo,
  onMove,
  onUserMoveEnd,
  showControls = true,
  showFullscreen = false,
  controlsPosition = "top-right",
  cooperativeGestures = false,
  mapStyle = DEFAULT_MAP_STYLE,
  interactive = true,
  className,
  style,
  children,
}: MapProps) {
  const mapRef = useRef<MapRef>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Latest fit-bounds/padding, read by the fullscreen transition and the load
  // handler without re-firing on every change.
  const fitBoundsRef = useRef(fitBounds);
  fitBoundsRef.current = fitBounds;
  const fitBoundsPaddingRef = useRef(fitBoundsPadding);
  fitBoundsPaddingRef.current = fitBoundsPadding;

  // The first fit is instant: a fresh load should render already framed on the results, not animate
  // a jarring zoom-in from the default world view. The fit runs once hits resolve (map already
  // teleported into its real-sized slot), so framing is correct. Later fits — a new Where selection
  // or a deliberate refit — keep the 500ms animation, where motion reads as intentional. Padding is
  // a dependency so a padding change alone (e.g. crossing to the mobile sheet layout, which reserves
  // the lower map band) re-frames the results instead of leaving pins hidden behind the sheet.
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (!fitBounds) return;
    // Before the map exists (a consumer whose bounds are known at creation), leave the
    // initial-fit flag unset so the load handler below applies the pending fit instead.
    const map = mapRef.current;
    if (!map) return;
    const isInitialFit = !didInitialFitRef.current;
    didInitialFitRef.current = true;
    map.fitBounds(fitBounds, {
      padding: fitBoundsPadding,
      duration: isInitialFit ? 0 : FIT_BOUNDS_DURATION,
    });
  }, [fitBounds, fitBoundsPadding]);

  // Zoom-preserving pan to a point (e.g. a selected pin) offset by padding so it clears the
  // sheet/card. Programmatic → `onMoveEnd`'s null-originalEvent guard suppresses any refetch.
  // `retainPadding: false` uses the padding for THIS ease only — the singleton map is shared with
  // the desktop inline view, which must not inherit the sheet's large asymmetric bottom padding.
  useEffect(() => {
    if (!recenterTo) return;
    mapRef.current?.easeTo({
      center: [recenterTo.longitude, recenterTo.latitude],
      padding: recenterTo.padding ?? 0,
      retainPadding: false,
      duration: FIT_BOUNDS_DURATION,
    });
  }, [recenterTo]);

  // Toggled at runtime rather than handed to `<MapGL>` as a constructor option: this Map is a
  // shared singleton, and the constructor path permanently adds `mapboxgl-scrollable-page`
  // (`touch-action: pan-x pan-y`) to the canvas container, killing one-finger pan for the
  // consumers that want cooperative gestures off. `onLoad` re-applies for a prop set before the
  // map existed, via this ref — the singleton mounts once, so a closure would freeze the first
  // value.
  const cooperativeGesturesRef = useRef(cooperativeGestures);
  cooperativeGesturesRef.current = cooperativeGestures;
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (map) applyCooperativeGestures(map, cooperativeGestures);
  }, [cooperativeGestures]);

  const toggleFullscreen = useCallback(() => setIsFullscreen((prev) => !prev), []);

  // This Map is a single persistent instance teleported between pages; clearing the
  // overlay (consumer unmount on navigation) must drop fullscreen so the next page
  // doesn't inherit a stuck full-viewport overlay. Reset the initial-fit flag too, so the
  // NEXT search page's first fit snaps instantly rather than inheriting this page's "already
  // fitted" state and animating a jarring fly on every page after the first.
  useEffect(() => {
    if (children == null) {
      setIsFullscreen(false);
      didInitialFitRef.current = false;
    }
  }, [children]);

  // Fullscreen flips the wrapper size, so re-read the container and re-fit the result
  // envelope to the new bounds. Skip the initial mount (no size change happened) so
  // every map mount doesn't fire a spurious resize. While fullscreen, Esc also exits.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (!isFullscreen) return;
    }

    const id = requestAnimationFrame(() => {
      mapRef.current?.resize();
      if (fitBoundsRef.current) {
        mapRef.current?.fitBounds(fitBoundsRef.current, {
          padding: fitBoundsPadding,
          duration: FIT_BOUNDS_DURATION,
        });
      }
      window.dispatchEvent(new Event("resize"));
    });
    if (!isFullscreen) return () => cancelAnimationFrame(id);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isFullscreen, fitBoundsPadding]);

  const resolvedInitialViewState =
    initialBounds != null
      ? { bounds: initialBounds, fitBoundsOptions: { padding: fitBoundsPadding } }
      : initialViewState;

  // Pass the click through as `originalEvent` so the resulting `moveend` is treated as
  // user-initiated and re-runs `onUserMoveEnd` (bounded search), matching the behaviour
  // of Mapbox's stock NavigationControl zoom buttons.
  const handleZoom = useCallback((direction: "in" | "out", event: MouseEvent) => {
    const map = mapRef.current;
    if (!map) return;
    const eventData = { originalEvent: event.nativeEvent };
    if (direction === "in") map.zoomIn({}, eventData);
    else map.zoomOut({}, eventData);
  }, []);

  const content = (
    <>
      <MapGL
        ref={mapRef}
        mapboxAccessToken={mapboxAccessToken}
        mapStyle={mapStyle}
        projection="mercator"
        interactive={interactive}
        // Flat mercator maps gain nothing from tilt/rotation — disable the perspective
        // gestures (drag-rotate, two-finger pitch) and lock pitch to 0. Pan and zoom stay.
        dragRotate={false}
        touchPitch={false}
        pitchWithRotate={false}
        maxPitch={0}
        onLoad={(event) => {
          event.target.touchZoomRotate.disableRotation();
          applyCooperativeGestures(event.target, cooperativeGesturesRef.current);
          // A consumer whose bounds are already known at map creation (e.g. a detail-page
          // map with server-provided coordinates) sets `fitBounds` before the map exists —
          // the fit effect fires against a null ref and the reference never changes again.
          // Apply the pending fit at load, instantly, like any other initial fit.
          if (!didInitialFitRef.current && fitBoundsRef.current) {
            didInitialFitRef.current = true;
            event.target.fitBounds(fitBoundsRef.current, { padding: fitBoundsPaddingRef.current, duration: 0 });
          }
        }}
        {...viewState}
        initialViewState={viewState ? undefined : resolvedInitialViewState}
        onMove={onMove}
        onMoveEnd={(event) => {
          // Programmatic moves (fitBounds, flyTo) leave originalEvent null.
          if (!onUserMoveEnd) return;
          const originalEvent = (event as { originalEvent?: { type?: string } }).originalEvent;
          if (!originalEvent) return;
          // A window/viewport resize makes mapbox-gl fire `moveend` with the DOM resize event as
          // `originalEvent` (its internal `_onWindowResize` calls `resize({ originalEvent })`), which
          // would otherwise be misread as a user pan and refetch results with the new, wrong bounds.
          // The two synthetic `window.dispatchEvent(new Event("resize"))` (teleport, fullscreen) route
          // through the same path. Ignore resize-origin moves — only genuine gestures (mouse/touch/
          // wheel/keyboard, and the zoom buttons' injected click) should drive bounded search.
          if (originalEvent.type === "resize" || originalEvent.type === "orientationchange") return;
          onUserMoveEnd(event);
        }}
        style={{ width: "100%", height: "100%" }}
      >
        {children}
      </MapGL>
      {showControls && interactive && (
        <MapControls
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onZoomIn={(event) => handleZoom("in", event)}
          onZoomOut={(event) => handleZoom("out", event)}
          position={controlsPosition}
          showFullscreen={showFullscreen}
        />
      )}
    </>
  );

  // Expanded view (Airbnb-style): cover the content area below the top bar, keeping the
  // map's own rounded corners — not edge-to-edge. Gap on the sides and bottom only; the
  // top sits flush below `--frame-topbar-h`, whose owner (the sticky header) supplies the
  // top gap via its own bottom padding so fullscreen and the inline map align.
  if (isFullscreen) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-30 bg-white px-5 pb-5 md:px-8 md:pb-8"
        style={{ top: "var(--frame-topbar-h, 5.5rem)" }}
      >
        <div className={`relative size-full bg-white ${className ?? ""}`}>{content}</div>
      </div>
    );
  }

  return (
    <div className={`relative ${className ?? ""}`} style={style}>
      {content}
    </div>
  );
}

export type { LngLatBoundsLike, ViewState, ViewStateChangeEvent } from "react-map-gl/mapbox";
export { Marker } from "react-map-gl/mapbox";
